import React, { useCallback, useEffect, useRef, useState } from "react";
import { ReactComponent as Logo } from 'src/assets/logo2.svg';
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    motion, AnimatePresence,
    useMotionValue, useSpring, useTransform, useAnimationFrame,
    MotionValue,
} from "framer-motion";
import { css } from "@emotion/react";

/* ─────────────────────────────────────────
   3D 버블 — 각 버블이 독립적인 사인파 float +
   마우스 위치에 따른 시차(parallax) 반응
───────────────────────────────────────── */
interface BubbleProps {
    size: number;
    depth: number;                         // 마우스 시차 강도 (px)
    top?: string; bottom?: string;
    left?: string; right?: string;
    floatSpeed?: number;                   // ms per cycle
    floatAmplitude?: number;               // px
    mouseX: MotionValue<number>;
    mouseY: MotionValue<number>;
}

const Bubble: React.FC<BubbleProps> = ({
    size, depth,
    top, bottom, left, right,
    floatSpeed = 2200,
    floatAmplitude = 14,
    mouseX, mouseY,
}) => {
    const floatY = useMotionValue(0);
    const phase = useRef(Math.random() * Math.PI * 2);

    // 사인파 부유 — CSS animation 대신 framer useAnimationFrame 사용 (마우스 Y와 합산 가능)
    useAnimationFrame((time) => {
        floatY.set(Math.sin(time / floatSpeed + phase.current) * floatAmplitude);
    });

    // 마우스 시차 (depth 클수록 더 크게 반응 → 작은 버블이 "가까이" 있는 느낌)
    const tx = useTransform(mouseX, (v) => v * depth);
    const ty = useTransform(mouseY, (v) => v * depth);
    const sx = useSpring(tx, { stiffness: 40, damping: 24 });
    const sy = useSpring(ty, { stiffness: 40, damping: 24 });

    // floatY + 마우스 Y 합산
    const combinedY = useTransform(
        [sy, floatY],
        ([a, b]: number[]) => a + b,
    );

    const s = size;
    return (
        <motion.div
            style={{
                position: 'absolute',
                width: s, height: s,
                borderRadius: '50%',
                top, bottom, left, right,
                x: sx,
                y: combinedY,
                pointerEvents: 'none',
                // ── 3D 구체 표현 ──
                // 1) 좌상단 흰 하이라이트 (빛 반사)
                // 2) 우하단 초록 환경 반사
                // 3) 구체 본체 그라디언트
                background: [
                    'radial-gradient(circle at 34% 30%, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0) 50%)',
                    'radial-gradient(circle at 66% 68%, rgba(25,207,122,0.24) 0%, transparent 46%)',
                    `radial-gradient(circle at 50% 50%, rgba(218,246,234,0.88) 0%, rgba(175,237,210,0.65) 54%, rgba(95,208,160,0.18) 100%)`,
                ].join(', '),
                boxShadow: [
                    `inset -${(s * 0.032).toFixed(1)}px -${(s * 0.042).toFixed(1)}px ${(s * 0.1).toFixed(1)}px rgba(25,207,122,0.18)`,
                    `inset ${(s * 0.03).toFixed(1)}px ${(s * 0.04).toFixed(1)}px ${(s * 0.12).toFixed(1)}px rgba(255,255,255,0.8)`,
                    `0 ${(s * 0.055).toFixed(1)}px ${(s * 0.17).toFixed(1)}px rgba(25,207,122,0.13)`,
                    '0 2px 6px rgba(0,0,0,0.04)',
                ].join(', '),
                border: '1px solid rgba(255,255,255,0.6)',
            }}
        />
    );
};

/* ─────────────────────────────────────────
   스타일
───────────────────────────────────────── */
const bgCss = css`
    position: fixed;
    inset: 0;
    background: linear-gradient(149deg, #f1f5f2 0%, #e6f5ed 55%, #d4f0e2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
`;

const cardCss = css`
    position: relative;
    z-index: 10;
    width: 440px;
    background: rgba(255,255,255,0.82);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border: 1px solid rgba(25,207,122,0.15);
    border-radius: 28px;
    padding: 52px 48px;
    box-shadow:
        0 4px 6px rgba(0,0,0,0.03),
        0 20px 60px rgba(25,207,122,0.1),
        0 40px 80px rgba(0,0,0,0.07);

    &::before {
        content: '';
        position: absolute;
        top: 0; left: 50%;
        transform: translateX(-50%);
        width: 55%; height: 2px;
        border-radius: 0 0 4px 4px;
        background: linear-gradient(90deg, transparent, #19CF7A, transparent);
    }

    .input-group { display: flex; flex-direction: column; gap: 6px; }

    label {
        color: #6b7280; font-size: 11px; font-weight: 700;
        letter-spacing: 0.9px; text-transform: uppercase;
    }

    .input-field {
        width: 100%; background: #f8faf9;
        border: 1.5px solid #e5ece8; border-radius: 12px;
        padding: 13px 16px; color: #14213d; font-size: 15px;
        outline: none; box-sizing: border-box;
        transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
    }
    .input-field::placeholder { color: #b0bab5; }
    .input-field:focus {
        border-color: #19CF7A; background: #fff;
        box-shadow: 0 0 0 3px rgba(25,207,122,0.12);
    }

    .btn-login {
        width: 100%; height: 52px;
        background: linear-gradient(135deg, #19CF7A 0%, #0fb865 100%);
        border: none; border-radius: 14px;
        color: white; font-size: 15px; font-weight: 700;
        cursor: pointer; letter-spacing: 0.4px;
        box-shadow: 0 8px 24px rgba(25,207,122,0.35);
        transition: transform 0.15s ease, box-shadow 0.2s ease;
    }
    .btn-login:hover { transform: translateY(-2px); box-shadow: 0 14px 32px rgba(25,207,122,0.45); }
    .btn-login:active { transform: translateY(0); }

    .btn-outline {
        width: 100%; height: 46px;
        background: transparent; border: 1.5px solid #d1d5db;
        border-radius: 12px; color: #6b7280; font-size: 14px;
        cursor: pointer;
        transition: border-color 0.2s, color 0.2s, background 0.2s;
    }
    .btn-outline:hover { border-color: #19CF7A; color: #19CF7A; background: rgba(25,207,122,0.04); }

    .divider {
        display: flex; align-items: center; gap: 12px;
        color: #c4cdc8; font-size: 11px; font-weight: 600; letter-spacing: 0.8px;
    }
    .divider::before, .divider::after { content: ''; flex: 1; height: 1px; background: #e5ece8; }

    .otp-badge {
        display: inline-flex; align-items: center; gap: 6px;
        background: rgba(25,207,122,0.08); border: 1px solid rgba(25,207,122,0.2);
        border-radius: 8px; padding: 6px 12px;
        color: #0da85e; font-size: 13px; font-weight: 600; margin-bottom: 4px;
    }

    .qr-box {
        background: #f8faf9; border: 1px solid #e5ece8; border-radius: 16px;
        padding: 20px; display: flex; flex-direction: column; align-items: center; gap: 10px;
    }

    .secret-key {
        background: #f0faf5; border: 1px solid rgba(25,207,122,0.2);
        border-radius: 8px; padding: 10px 14px;
        color: #4b7c63; font-family: monospace; font-size: 13px;
        word-break: break-all; text-align: center;
    }
`;

/* ─────────────────────────────────────────
   메인 컴포넌트
───────────────────────────────────────── */
const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [qrUrl, setQrUrl] = useState('');
    const [secretKey, setSecretKey] = useState('');
    const navigate = useNavigate();

    // 마우스 위치 정규화 (-1 ~ 1)
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        mouseX.set((e.clientX / window.innerWidth) * 2 - 1);
        mouseY.set((e.clientY / window.innerHeight) * 2 - 1);
    }, [mouseX, mouseY]);

    useEffect(() => {
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, [handleMouseMove]);

    const handleRegisterOtp = async () => {
        try {
            const loginResponse = await axios.post('/api/login', { id: email, password });
            const adminToken = loginResponse.data.token;
            const res = await axios.post('/api/admin/otp/register', { username: email }, {
                headers: { Authorization: `Bearer ${adminToken}` },
            });
            setQrUrl(res.data.qrUrl);
            setSecretKey(res.data.secret);
            alert('OTP 등록 완료. QR코드를 Google Authenticator로 스캔하세요.');
        } catch (err) {
            console.error(err);
            alert('OTP 등록 실패');
        }
    };

    const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setShowOtpInput(true);
    };

    const handleVerifyOtp = async () => {
        try {
            const res = await axios.post('/api/admin/otp/verify', { username: email, otp });
            if (res.data.success) {
                sessionStorage.setItem('adminToken', res.data.token);
                sessionStorage.setItem('adminEmail', email);
                navigate('/admin');
            } else {
                alert('OTP 인증 실패');
                navigate('/admin');
            }
        } catch (err) {
            console.error(err);
            alert('OTP 인증 요청 실패');
        }
    };

    return (
        <div css={bgCss}>
            {/*
              버블 배치: size가 클수록 depth 작음 → 멀리 있는 느낌 (느리게 반응)
                        size가 작을수록 depth 큼  → 가까이 있는 느낌 (빠르게 반응)
              floatSpeed가 작을수록 빠르게 흔들림
            */}
            <Bubble size={280} depth={14} top="-70px"   right="-50px"  floatSpeed={3000} floatAmplitude={16} mouseX={mouseX} mouseY={mouseY} />
            <Bubble size={200} depth={26} bottom="-55px" left="-50px"  floatSpeed={2600} floatAmplitude={13} mouseX={mouseX} mouseY={mouseY} />
            <Bubble size={140} depth={40} top="10%"     left="5%"      floatSpeed={2200} floatAmplitude={11} mouseX={mouseX} mouseY={mouseY} />
            <Bubble size={110} depth={52} bottom="14%"  right="5%"     floatSpeed={1900} floatAmplitude={9}  mouseX={mouseX} mouseY={mouseY} />
            <Bubble size={72}  depth={68} top="44%"     right="10%"    floatSpeed={1700} floatAmplitude={7}  mouseX={mouseX} mouseY={mouseY} />
            <Bubble size={48}  depth={82} top="20%"     right="24%"    floatSpeed={1500} floatAmplitude={5}  mouseX={mouseX} mouseY={mouseY} />
            <Bubble size={36}  depth={95} bottom="25%"  left="20%"     floatSpeed={1300} floatAmplitude={4}  mouseX={mouseX} mouseY={mouseY} />

            {/* 카드 */}
            <motion.div
                css={cardCss}
                initial={{ opacity: 0, y: 28, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
                {/* 로고 */}
                <div className="flex flex-col items-center mb-10">
                    <div
                        className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-4"
                        style={{ boxShadow: '0 4px 16px rgba(25,207,122,0.2), 0 1px 4px rgba(0,0,0,0.06)' }}
                    >
                        <Logo style={{ width: 36, height: 36 }} />
                    </div>
                    <p className="text-[22px] font-bold text-[#14213D] tracking-tight">BookMeet Admin</p>
                    <p className="text-[13px] text-[#9ca3af] mt-1">북밋 관리자 로그인</p>
                </div>

                <AnimatePresence mode="wait">
                    {!showOtpInput ? (
                        <motion.form
                            key="login-form"
                            initial={{ opacity: 0, x: -16 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -16 }}
                            transition={{ duration: 0.28 }}
                            onSubmit={handleLogin}
                            className="flex flex-col gap-5"
                        >
                            <div className="input-group">
                                <label>ID</label>
                                <input type="text" className="input-field" placeholder="admin@bookmeet.com"
                                    value={email} onChange={e => setEmail(e.target.value)} required />
                            </div>
                            <div className="input-group">
                                <label>Password</label>
                                <input type="password" className="input-field" placeholder="••••••••"
                                    value={password} onChange={e => setPassword(e.target.value)} required />
                            </div>
                            <button type="submit" className="btn-login mt-1">로그인</button>
                            <div className="divider">OTP 설정</div>
                            <button type="button" className="btn-outline" onClick={handleRegisterOtp}>
                                OTP 등록
                            </button>
                            {qrUrl && (
                                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                                    className="mt-1 flex flex-col gap-3">
                                    <div className="qr-box">
                                        <p className="text-[#555] text-[13px] text-center">
                                            Google Authenticator로 스캔하세요
                                        </p>
                                        <img src={qrUrl} alt="QR Code" className="w-40 h-40" />
                                    </div>
                                    <div className="secret-key">시크릿 키: {secretKey}</div>
                                </motion.div>
                            )}
                        </motion.form>
                    ) : (
                        <motion.div
                            key="otp-form"
                            initial={{ opacity: 0, x: 16 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 16 }}
                            transition={{ duration: 0.28 }}
                            className="flex flex-col gap-5"
                        >
                            <div className="otp-badge"><span>🔐</span><span>2단계 인증</span></div>
                            <p className="text-[#6b7280] text-[14px] leading-relaxed">
                                OTP 앱에 표시된 6자리 코드를 입력하세요.
                            </p>
                            <div className="input-group">
                                <label>OTP 코드</label>
                                <input type="text" className="input-field" placeholder="000000"
                                    maxLength={6} value={otp}
                                    onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                                    style={{ letterSpacing: '8px', textAlign: 'center', fontSize: '22px', fontWeight: 700 }}
                                />
                            </div>
                            <button type="button" className="btn-login" onClick={handleVerifyOtp}>인증 확인</button>
                            <button type="button" className="btn-outline" onClick={() => setShowOtpInput(false)}>
                                ← 뒤로 가기
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
};

export default Login;
